(() => {
  if (customElements.get('pawsmo-cart-upsells')) return;

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;',
    })[character]);

  const formatMoney = (amount) => {
    const currency = window.Shopify?.currency?.active || 'INR';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format((Number(amount) || 0) / 100);
  };

  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const imageUrl = (image) => (typeof image === 'string' ? image : image?.src || image?.url || '');
  const isColorOption = (name = '') => /colou?r/i.test(name);

  class PawsmoCartUpsells extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.ratingByHandle = this.getRatings();
      this.onUpsellClick = this.onUpsellClick.bind(this);
      this.addEventListener('click', this.onUpsellClick);
      this.loadProducts();
    }

    getRatings() {
      try {
        return new Map(
          JSON.parse(this.dataset.ratings || '[]')
            .filter((rating) => rating?.handle)
            .map((rating) => [rating.handle, rating])
        );
      } catch (error) {
        return new Map();
      }
    }

    async loadProducts() {
      const handles = (this.dataset.products || '')
        .split(',')
        .map((handle) => handle.trim())
        .filter(Boolean);

      if (!handles.length) {
        this.hidden = true;
        return;
      }

      try {
        const cartProductIds = new Set(
          (this.dataset.cartProductIds || '')
            .split(',')
            .map((id) => Number(id))
            .filter(Boolean)
        );
        const responses = await Promise.all(
          handles.map(async (handle) => {
            const response = await fetch(`${rootUrl()}products/${encodeURIComponent(handle)}.js`, {
              credentials: 'same-origin',
            });
            if (!response.ok) throw new Error(`Unable to load ${handle}`);
            return response.json();
          })
        );
        const products = responses.filter(
          (product) =>
            product.available &&
            !cartProductIds.has(Number(product.id)) &&
            product.variants?.some((variant) => variant.available)
        );

        if (!products.length) {
          this.hidden = true;
          return;
        }

        this.products = new Map(products.map((product) => [product.handle, product]));
        this.renderProducts(products.slice(0, 6));
      } catch (error) {
        this.hidden = true;
      }
    }

    renderProducts(products) {
      const heading = escapeHtml(this.dataset.heading || 'Lowest price on the website');
      const subheading = escapeHtml(this.dataset.subheading || 'Cart-Only Deals! Limited time only!');
      this.innerHTML = `
        <div class="pawsmo-upsells">
          <p class="pawsmo-upsells__heading">
            <span>${heading}<small>${subheading}</small></span>
            <span class="pawsmo-upsells__badge" aria-hidden="true"><span>PRICE</span><span>DROP</span></span>
          </p>
          <div class="pawsmo-upsells__list" role="list">
            ${products.map((product) => this.productCard(product)).join('')}
          </div>
        </div>
      `;

    }

    productCard(product) {
      const variant = product.variants.find((item) => item.available) || product.variants[0];
      const image = product.featured_image || product.images?.[0];
      const compareAt = Number(variant.compare_at_price) > Number(variant.price)
        ? `<span class="pawsmo-upsell-card__compare">${formatMoney(variant.compare_at_price)}</span>`
        : '';
      const discount = Number(variant.compare_at_price) > Number(variant.price)
        ? Math.round((1 - Number(variant.price) / Number(variant.compare_at_price)) * 100)
        : 0;

      return `
        <article class="pawsmo-upsell-card" role="listitem">
          <div class="pawsmo-upsell-card__image">
            ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="264" height="264">` : ''}
          </div>
          <h4>${escapeHtml(product.title)}</h4>
          <div class="pawsmo-upsell-card__price">${formatMoney(variant.price)}${compareAt}</div>
          ${discount > 0 ? `<span class="pawsmo-upsell-card__discount">${discount}% OFF</span>` : ''}
          <button type="button" class="pawsmo-upsell-card__add" data-pawsmo-add="${escapeHtml(product.handle)}">
            ADD
          </button>
        </article>
      `;
    }

    onUpsellClick(event) {
      const button = event.target.closest('[data-pawsmo-add]');
      if (!button) return;

      const product = this.products?.get(button.dataset.pawsmoAdd);
      if (!product) return;

      if (product.variants.length === 1) {
        this.addVariant(product.variants[0].id, button);
      } else {
        this.openVariantPicker(product);
      }
    }

    async addVariant(variantId, button) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || !variantId) return;

      const originalText = button?.textContent;
      if (button) {
        button.disabled = true;
        button.textContent = 'ADDING';
      }

      try {
        const body = {
          items: [{ id: variantId, quantity: 1 }],
          sections: cartDrawer.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };
        const response = await fetch(`${routes.cart_add_url}`, {
          ...fetchConfig(),
          headers: { ...fetchConfig().headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const state = await response.json();
        if (state.status) throw new Error(state.description || 'Unable to add this product');

        cartDrawer.renderContents(state);
        this.closeVariantPicker();
      } catch (error) {
        if (button) button.textContent = 'TRY AGAIN';
      } finally {
        if (button && document.body.contains(button)) {
          button.disabled = false;
          window.setTimeout(() => {
            if (document.body.contains(button) && button.textContent !== 'TRY AGAIN') button.textContent = originalText;
          }, 500);
        }
      }
    }

    openVariantPicker(product) {
      this.closeVariantPicker();

      const modal = document.createElement('div');
      modal.className = 'pawsmo-quick-cart-modal';
      modal.id = 'PawsmoQuickCartModal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', `Choose options for ${product.title}`);
      modal.innerHTML = '<div class="pawsmo-quick-cart-modal__panel"><div data-pawsmo-modal-content></div></div>';
      document.body.appendChild(modal);

      const selectedVariant = product.variants.find((variant) => variant.available) || product.variants[0];
      const selectedOptions = [...selectedVariant.options];
      const close = () => this.closeVariantPicker();
      const render = () => this.renderVariantPicker(modal, product, selectedOptions);

      modal.addEventListener('click', (event) => {
        if (event.target === modal || event.target.closest('[data-pawsmo-modal-close]')) {
          close();
          return;
        }

        const optionButton = event.target.closest('[data-pawsmo-option]');
        if (optionButton && !optionButton.disabled) {
          selectedOptions[Number(optionButton.dataset.optionIndex)] = optionButton.dataset.pawsmoOption;
          render();
          return;
        }

        const addButton = event.target.closest('[data-pawsmo-modal-add]');
        if (addButton) {
          const variant = this.findVariant(product, selectedOptions);
          if (variant?.available) this.addVariant(variant.id, addButton);
        }
      });

      const onKeyDown = (event) => {
        if (event.key === 'Escape') close();
      };
      modal.addEventListener('pawsmo:close', () => document.removeEventListener('keydown', onKeyDown), { once: true });
      document.addEventListener('keydown', onKeyDown);
      render();
      modal.querySelector('[data-pawsmo-modal-close]')?.focus();
    }

    closeVariantPicker() {
      const modal = document.getElementById('PawsmoQuickCartModal');
      if (!modal) return;
      modal.dispatchEvent(new Event('pawsmo:close'));
      modal.remove();
    }

    findVariant(product, selectedOptions) {
      return product.variants.find((variant) =>
        variant.options.every((value, index) => value === selectedOptions[index])
      );
    }

    renderVariantPicker(modal, product, selectedOptions) {
      const content = modal.querySelector('[data-pawsmo-modal-content]');
      const variant = this.findVariant(product, selectedOptions);
      const images = product.images?.slice(0, 4) || [];
      const variantImage = imageUrl(variant?.featured_image) || imageUrl(product.featured_image);
      const gallery = [...new Set([variantImage, ...images.map(imageUrl)].filter(Boolean))].slice(0, 5);

      content.innerHTML = `
        <div class="pawsmo-quick-cart-modal__top">
          <h3>${escapeHtml(product.title)}</h3>
          <button type="button" class="pawsmo-quick-cart-modal__close" data-pawsmo-modal-close aria-label="Close product options">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="pawsmo-quick-cart-modal__gallery" role="list" aria-label="Product images">
          ${gallery.map((image, index) => `<span class="pawsmo-quick-cart-modal__thumbnail${index === 0 ? ' is-active' : ''}" role="listitem"><img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" width="160" height="160" loading="${index === 0 ? 'eager' : 'lazy'}"></span>`).join('')}
        </div>
        ${(product.options || []).map((name, index) => this.optionGroup(product, name, index, selectedOptions)).join('')}
        <div class="pawsmo-quick-cart-modal__price">
          <span>${variant ? formatMoney(variant.price) : 'Unavailable'}</span>
          ${variant && Number(variant.compare_at_price) > Number(variant.price) ? `<span class="pawsmo-quick-cart-modal__compare">${formatMoney(variant.compare_at_price)}</span>` : ''}
        </div>
        <button type="button" class="pawsmo-quick-cart-modal__add" data-pawsmo-modal-add ${variant?.available ? '' : 'disabled'}>
          ${variant?.available ? 'ADD TO CART' : 'SOLD OUT'}
        </button>
      `;
    }

    optionGroup(product, name, index, selectedOptions) {
      const values = [...new Set(product.variants.map((variant) => variant.options[index]))];
      const colorOption = isColorOption(name);
      return `
        <div class="pawsmo-quick-cart-modal__option${colorOption ? ' is-color-option' : ''}">
          <span class="pawsmo-quick-cart-modal__option-label">Choose ${escapeHtml(name)}: <strong>${escapeHtml(selectedOptions[index] || '')}</strong></span>
          <div class="pawsmo-quick-cart-modal__option-values">
            ${values.map((value) => {
              const compatible = product.variants.some((variant) =>
                variant.available && variant.options.every((optionValue, optionIndex) =>
                  optionIndex === index ? optionValue === value : optionValue === selectedOptions[optionIndex]
                )
              );
              const image = imageUrl(
                product.variants.find((variant) => variant.options[index] === value && imageUrl(variant.featured_image))?.featured_image
              );
              const swatch = image
                ? `<span class="pawsmo-quick-cart-modal__color-swatch" style="background-image:url('${escapeHtml(image)}')"></span>`
                : `<span class="pawsmo-quick-cart-modal__color-swatch" style="background-color:${escapeHtml(value)}"></span>`;
              const classNames = `${value === selectedOptions[index] ? 'is-selected' : ''}${colorOption ? ' is-color' : ''}`;
              return `<button type="button" data-pawsmo-option="${escapeHtml(value)}" data-option-index="${index}" class="${classNames}" ${compatible ? '' : 'disabled'} aria-pressed="${value === selectedOptions[index]}">${colorOption ? `${swatch}<span class="visually-hidden">${escapeHtml(value)}</span>` : escapeHtml(value)}</button>`;
            }).join('')}
          </div>
        </div>
      `;
    }
  }

  class PawsmoCartFrequentlyBought extends PawsmoCartUpsells {
    renderProducts(products) {
      const heading = escapeHtml(this.dataset.heading || '66% of shoppers added these too...');
      this.innerHTML = `
        <div class="pawsmo-frequently-bought">
          <h3 class="pawsmo-frequently-bought__heading">${heading}</h3>
          <div class="pawsmo-frequently-bought__list" role="list">
            ${products.map((product) => this.productCard(product)).join('')}
          </div>
        </div>
      `;
    }

    productCard(product) {
      const variant = product.variants.find((item) => item.available) || product.variants[0];
      const image = product.featured_image || product.images?.[0];
      const compareAt = Number(variant.compare_at_price) > Number(variant.price)
        ? `<span class="pawsmo-frequently-bought__compare">${formatMoney(variant.compare_at_price)}</span>`
        : '';
      const review = this.ratingByHandle?.get(product.handle);
      const rating = Number(review?.rating);
      const reviewCount = Number(review?.count);
      const ratingLabel = Number.isFinite(rating) && rating > 0
        ? rating.toFixed(1).replace(/\.0$/, '')
        : '';
      const ratingBadge = this.dataset.showRating === 'true' && ratingLabel && Number.isFinite(reviewCount) && reviewCount > 0
        ? `<span class="pawsmo-frequently-bought__rating"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3.1 2.54 5.15 5.69.83-4.12 4.02.97 5.67L12 16.1l-5.08 2.67.97-5.67L3.77 9.08l5.69-.83L12 3.1Z" fill="currentColor"/></svg><span>${escapeHtml(ratingLabel)}</span>${Number.isFinite(reviewCount) && reviewCount > 0 ? `<small>(${escapeHtml(Math.round(reviewCount))})</small>` : ''}</span>`
        : '';

      return `
        <article class="pawsmo-frequently-bought__card" role="listitem">
          <div class="pawsmo-frequently-bought__image">
            ${ratingBadge}
            ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}" loading="lazy" width="264" height="264">` : ''}
          </div>
          <h4>${escapeHtml(product.title)}</h4>
          <div class="pawsmo-frequently-bought__price">${compareAt}${formatMoney(variant.price)}</div>
          <button type="button" class="pawsmo-frequently-bought__add" data-pawsmo-add="${escapeHtml(product.handle)}">ADD</button>
        </article>
      `;
    }
  }

  customElements.define('pawsmo-cart-upsells', PawsmoCartUpsells);
  customElements.define('pawsmo-cart-frequently-bought', PawsmoCartFrequentlyBought);
})();
